import { Link } from "react-router-dom";
import { useState } from "react";
import { Twitter, Instagram, Linkedin, Youtube, ArrowUpRight, Loader2, MapPin, Phone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FOOTER_NAV } from "@/lib/content";

const navKey = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

function NewsletterForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <p data-testid="newsletter-success" className="text-sm text-ptf-accent leading-relaxed">
        {t("footer.newsletter_success")}
      </p>
    );
  }

  return (
    <form onSubmit={submit} data-testid="newsletter-form" className="flex max-w-sm">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("footer.newsletter_placeholder", { defaultValue: "you@example.com" })}
        data-testid="newsletter-email"
        className="flex-1 min-w-0 bg-transparent border-b border-ptf-bg/30 py-2.5 text-sm outline-none focus:border-ptf-accent placeholder:text-ptf-bg/35 transition-colors"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="newsletter-submit"
        className="ml-3 shrink-0 inline-flex items-center gap-2 bg-ptf-accent text-ptf-primary px-4 py-2.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] hover:brightness-95 disabled:opacity-60 transition-all"
      >
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {t("footer.newsletter_button", { defaultValue: "Notify me" })}
      </button>
    </form>
  );
}

const LEGAL_KEYS = [
  { key: "legal_privacy", to: "/legal/privacy" },
  { key: "legal_terms", to: "/legal/terms" },
  { key: "legal_disclaimer", to: "/legal/disclaimer" },
];

export default function Footer() {
  const { t } = useTranslation();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const email = settings?.contact_email || "contact@planettrotterfoundation.com";

  const socials = [
    { icon: Twitter, url: settings?.twitter, label: "Twitter" },
    { icon: Instagram, url: settings?.instagram, label: "Instagram" },
    { icon: Linkedin, url: settings?.linkedin, label: "LinkedIn" },
    { icon: Youtube, url: settings?.youtube, label: "YouTube" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-ptf-primary text-ptf-bg">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 pt-16 pb-8 lg:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src="/ptf-mark.png" alt="" className="h-14 w-14" />
              <span className="leading-none">
                <span className="block font-display font-extrabold text-2xl tracking-tight uppercase">Planet Trotter</span>
                <span className="block text-[0.65rem] tracking-[0.34em] uppercase text-ptf-bg/60 mt-1">Foundation</span>
              </span>
            </div>
            <p className="mt-5 font-serif-ed italic text-sm text-ptf-accent">
              {t("footer.tagline")}
            </p>
            <p className="mt-3 max-w-md text-ptf-bg/70 leading-relaxed text-sm">
              {t("footer.description")}
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-bg/50 mb-5">{t("footer.explore")}</p>
            <ul className="grid grid-cols-2 gap-y-3 gap-x-4">
              {FOOTER_NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-sm text-ptf-bg/80 hover:text-ptf-accent transition-colors">
                    {t(`nav.${navKey(item.label)}`, { defaultValue: item.label })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-bg/50 mb-5">{t("footer.get_in_touch")}</p>
            <a
              href={`mailto:${email}`}
              data-testid="footer-email"
              className="inline-flex items-center gap-1.5 text-lg hover:text-ptf-accent transition-colors break-all"
            >
              {email}
              <ArrowUpRight className="h-4 w-4 shrink-0" />
            </a>
            <div className="mt-4 space-y-2.5">
              {settings?.phone && (
                <a href={`tel:${settings.phone.replace(/\s+/g, "")}`} data-testid="footer-phone" className="flex items-start gap-2.5 text-sm text-ptf-bg/70 hover:text-ptf-accent transition-colors">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-ptf-accent/70" />
                  {settings.phone}
                </a>
              )}
              {settings?.address && (
                <p data-testid="footer-address" className="flex items-start gap-2.5 text-sm text-ptf-bg/70">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-ptf-accent/70" />
                  <span className="whitespace-pre-line">{settings.address}</span>
                </p>
              )}
            </div>
            <div className="mt-8 flex items-center gap-3">
              {socials.map(({ icon: Icon, url, label }) => (
                <a
                  key={label}
                  href={url || "#"}
                  target={url ? "_blank" : undefined}
                  rel={url ? "noopener noreferrer" : undefined}
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  onClick={(e) => { if (!url) e.preventDefault(); }}
                  className="grid place-items-center h-10 w-10 rounded-full border border-ptf-bg/25 text-ptf-bg/70 hover:border-ptf-accent hover:text-ptf-accent transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <div className="mt-10">
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-bg/50 mb-4">
                {t("footer.newsletter_heading")}
              </p>
              <NewsletterForm />
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-ptf-bg/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="text-xs text-ptf-bg/50">
              © {new Date().getFullYear()} {t("footer.copyright")}
            </span>
          </div>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 lg:pr-20">
            {LEGAL_KEYS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-xs text-ptf-bg/60 hover:text-ptf-accent transition-colors">
                  {t(`footer.${l.key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
